/**
 * Small, allocation-free helpers for row-major 2D scalar fields
 * (x fastest, then y). Shared by the SDF / intensity refinement passes
 * and (in a future PR) the explicit Interpolation tool, so the
 * central-difference gradient idiom lives in one place instead of
 * being re-derived at each call site.
 *
 * Pure and framework-free — unit-testable in isolation.
 */

/**
 * Central-difference gradient of a row-major 2D scalar `field` at the
 * interior pixel `idx` (= `y * width + x`). The pixel MUST NOT lie on
 * the outer 1-pixel border: central differences need a neighbour on
 * each side, so callers are responsible for skipping the border.
 *
 * Writes ∂/∂x into `out[0]` and ∂/∂y into `out[1]`, and returns the
 * gradient magnitude `√(gx² + gy²)`. `out` is a caller-owned scratch
 * buffer (length ≥ 2) reused across the loop, so the helper performs
 * no allocation in the hot path.
 *
 * Domain-specific gating (e.g. skipping pixels whose neighbours are
 * non-finite / out-of-volume, or below a noise floor) is the caller's
 * responsibility — this helper assumes the four neighbours are valid
 * samples and simply computes the difference.
 */
export function centralDifferenceGradient2D(
    field: ArrayLike<number>,
    idx: number,
    width: number,
    out: Float32Array | number[]
): number {
    const gx = (field[idx + 1] - field[idx - 1]) * 0.5;
    const gy = (field[idx + width] - field[idx - width]) * 0.5;
    out[0] = gx;
    out[1] = gy;
    return Math.sqrt(gx * gx + gy * gy);
}
